// src/client/renderer/vfx/flash-panel-pool.ts
//
// Plan 14b, Task 6.1 — Flash panel pool: screen-space zigzag mask shader +
// gsap intensity tween.
//
// Architecture:
//   Each emitter is a THREE.Mesh on a PlaneGeometry sized to fill the camera
//   near-plane. This produces a screen-space overlay effect — the mesh sits
//   directly in front of the camera at the near plane, matching its dimensions
//   exactly, so it covers the entire viewport.
//
//   Near-plane sizing:
//     PerspectiveCamera:
//       height = 2 * camera.near * Math.tan((camera.fov * Math.PI / 180) / 2)
//       width  = height * camera.aspect
//     OrthographicCamera:
//       width  = camera.right - camera.left
//       height = camera.top - camera.bottom
//
//   Mesh positioning:
//     Translate by camera.getWorldDirection() * camera.near to place the quad
//     at the near plane. Copy camera quaternion so the quad faces the camera.
//     Updated on every acquire() so the flash always snaps to the current
//     camera pose (no per-frame update needed — flash is short-lived).
//
//   Material: inline THREE.ShaderMaterial (NOT in shader registry — pool-private).
//   Uniforms: { uColor: vec3, uIntensity: float }
//   Fragment: zigzag mask via step(0.5 + 0.3 * sin(vUv.y * 30.0), vUv.x)
//     — inside mask: gl_FragColor = vec4(uColor, uIntensity)
//     — outside mask: discard
//   transparent: true + depthTest: false — standard screen-overlay VFX.
//
//   Variant token: args.token === 'red' → uColor = (1, 0.3, 0.3) (orbital
//   cannon charge). Default / 'white' → uColor = (1, 1, 1).
//
//   gsap timeline animates uIntensity:
//     0.00–0.05s  0 → 1   (snap-on, none ease)
//     0.05–0.15s  hold at 1
//     0.15–0.30s  1 → 0   (ease-out fade)
//     Total lifetime: 0.30s.
//
//   `isFinished` reads timeline.time() >= 0.3.
//   `release` clears the timeline handle (pool reuse; no GPU teardown).
//   `dispose` frees GPU resources: geometry.dispose() + material.dispose().
//
// maxActive: 1 (only one flash on screen at a time — visual clarity).

import * as THREE from 'three';
import type { ComicVFXPool, ComicVFXSpawnArgs } from './comic-vfx-types';
import type { GsapRegistry, GsapTimelineHandle } from './gsap-registry';
import type { GameEvent } from '../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────────────────────

export interface FlashPanelConfig {
  readonly gsapRegistry: GsapRegistry;
  /** Camera used to size the screen-space quad. Updated on every acquire. */
  readonly camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Maximum simultaneous active emitters (one flash on screen at a time). */
const MAX_ACTIVE = 1;

/** Total emitter lifetime in seconds. isFinished reads against this threshold. */
const LIFETIME = 0.3;

// ─────────────────────────────────────────────────────────────────────────────
// Inline GLSL shaders
//
// Vertex shader: standard pass-through with UV coordinates.
// Fragment shader: zigzag mask transition.
//   - vUv.y drives the zigzag via sin(vUv.y * 30.0), frequency 30 gives
//     30 half-cycles across the quad height — a fine-tooth zigzag edge
//   - step(0.5 + 0.3 * sin(vUv.y * 30.0), vUv.x) partitions the quad into
//     left (inside mask) vs right (outside mask) regions with a zigzag boundary
//   - Inside mask: render with uColor and uIntensity alpha
//   - Outside mask: discard (transparent — only the masked region flashes)
//
// transparent: true + depthTest: false — standard for screen-overlay VFX.
// ─────────────────────────────────────────────────────────────────────────────

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uIntensity;

  varying vec2 vUv;

  void main() {
    // Zigzag mask: boundary oscillates along y with amplitude 0.3, frequency 30.
    // step(threshold, vUv.x) → 1.0 when vUv.x >= threshold (inside mask).
    float threshold = 0.5 + 0.3 * sin(vUv.y * 30.0);
    float inside = step(threshold, vUv.x);

    if (inside < 0.5) {
      discard;
    }

    gl_FragColor = vec4(uColor, uIntensity);
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Variant colors
// ─────────────────────────────────────────────────────────────────────────────

/** White flash (default / 'white' token — death event). */
const COLOR_WHITE = new THREE.Color(1, 1, 1);

/** Red flash ('red' token — orbital cannon charge event). */
const COLOR_RED = new THREE.Color(1, 0.3, 0.3);

// ─────────────────────────────────────────────────────────────────────────────
// Internal userData shape
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterUserData {
  timelineHandle: GsapTimelineHandle | undefined;
  intensityProxy: { value: number };
}

// ─────────────────────────────────────────────────────────────────────────────
// Near-plane sizing helpers
// ─────────────────────────────────────────────────────────────────────────────

interface NearPlaneDimensions {
  width: number;
  height: number;
}

/**
 * Compute the width and height of the camera's near plane in world units.
 *
 * PerspectiveCamera:
 *   height = 2 * near * tan(fov_radians / 2)
 *   width  = height * aspect
 *
 * OrthographicCamera:
 *   width  = right - left
 *   height = top - bottom
 */
function getNearPlaneDimensions(
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
): NearPlaneDimensions {
  if (camera instanceof THREE.PerspectiveCamera) {
    const fovRad = (camera.fov * Math.PI) / 180;
    const height = 2 * camera.near * Math.tan(fovRad / 2);
    const width = height * camera.aspect;
    return { width, height };
  } else {
    // OrthographicCamera
    const width = camera.right - camera.left;
    const height = camera.top - camera.bottom;
    return { width, height };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

/** maxActive: 1 (only one flash on screen at a time — visual clarity). */
export function createFlashPanelPool(config: FlashPanelConfig): ComicVFXPool {
  const { gsapRegistry, camera } = config;
  const active = new Set<THREE.Object3D>();

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ComicVFXSpawnArgs): THREE.Mesh | null {
    // Return null BEFORE allocating resources to prevent leaks on over-cap.
    if (active.size >= MAX_ACTIVE) return null;

    // ── Determine color variant ────────────────────────────────────────
    // args.token === 'red' → orbital cannon red flash
    // default ('white' or undefined) → death white flash
    const baseColor = args.token === 'red' ? COLOR_RED : COLOR_WHITE;

    // ── Compute near-plane dimensions ──────────────────────────────────
    // Sized to exactly fill the camera near plane at acquire time.
    // This snaps the flash to the current viewport — no per-frame resize.
    const { width, height } = getNearPlaneDimensions(camera);

    // ── Build geometry ─────────────────────────────────────────────────
    const geometry = new THREE.PlaneGeometry(width, height);

    // ── Build inline ShaderMaterial (NOT in shader registry per spec) ──
    // Uniforms: uColor (flash tint), uIntensity (alpha driven by gsap)
    const intensityProxy = { value: 0.0 };

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        uColor: { value: baseColor.clone() },
        uIntensity: { value: 0.0 }, // starts transparent; gsap snaps on
      },
      transparent: true,
      depthTest: false, // screen-overlay VFX — never depth-clip
      depthWrite: false,
    });

    // ── Build Mesh ─────────────────────────────────────────────────────
    const mesh = new THREE.Mesh(geometry, material);

    // Position at near-plane center in front of camera.
    // camera.getWorldDirection() gives the look direction in world space.
    // We add near * direction to camera position to land on the near plane.
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    mesh.position.copy(camera.position).addScaledVector(direction, camera.near);

    // Orient mesh to face the camera (same quaternion).
    mesh.quaternion.copy(camera.quaternion);

    // ── Build gsap timeline ────────────────────────────────────────────
    //
    // Animation plan:
    //   0.00–0.05s  intensity 0 → 1   (snap-on, none ease)
    //   0.05–0.15s  hold at 1          (no explicit tween; gap)
    //   0.15–0.30s  intensity 1 → 0   (ease out)
    //   Total: 0.30s lifetime
    //
    // We animate a proxy object and sync to material.uniforms.uIntensity.value
    // in onUpdate, because gsap requires plain object properties.

    const handle = gsapRegistry.create('flash-panel');
    const tl = handle.timeline;

    // Snap-on: 0 → 1 over 0.05s
    tl.to(
      intensityProxy,
      {
        value: 1.0,
        duration: 0.05,
        ease: 'none',
        onUpdate: () => {
          material.uniforms['uIntensity'].value = intensityProxy.value;
        },
      },
      0,
    );

    // Fade-out: 1 → 0 over 0.15s, starting at t=0.15 (after 0.1s hold)
    tl.to(
      intensityProxy,
      {
        value: 0,
        duration: 0.15,
        ease: 'power2.out',
        onUpdate: () => {
          material.uniforms['uIntensity'].value = intensityProxy.value;
        },
      },
      0.15,
    );

    // ── Store handle + proxy in userData ──────────────────────────────
    const userData = mesh.userData as EmitterUserData;
    userData.timelineHandle = handle;
    userData.intensityProxy = intensityProxy;

    active.add(mesh);
    return mesh;
  }

  // ── update (no-op — gsap-registry drives all timelines) ────────────────

  function update(_emitter: THREE.Object3D, _deltaSeconds: number): void {
    // Registry's update(dt) drives all registered timelines.
    // Per-emitter update is a no-op for interface compatibility.
  }

  // ── isFinished ──────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const userData = emitter.userData as EmitterUserData;
    const handle = userData.timelineHandle;
    if (!handle) return true; // released — treat as finished
    return handle.timeline.time() >= LIFETIME;
  }

  // ── release (pool reuse — no GPU teardown) ──────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const userData = emitter.userData as EmitterUserData;
    if (userData.timelineHandle) {
      userData.timelineHandle.dispose();
      userData.timelineHandle = undefined;
    }
    active.delete(emitter);
    // Remove from scene parent if attached — caller (manager) handles this
    if (emitter.parent) {
      emitter.parent.remove(emitter);
    }
  }

  // ── dispose (GPU teardown) — idempotent ─────────────────────────────────
  //
  // The pool owns both the geometry and the inline ShaderMaterial.
  // Both are disposed here. Disposal is idempotent via THREE's internal
  // disposed flag (calling dispose() twice is a no-op after the first call).

  function dispose(emitter: THREE.Object3D): void {
    // Ensure release cleanup first (clears handle, removes from active set)
    release(emitter);

    const mesh = emitter as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  return Object.freeze({
    id: 'flash-panel' as const,
    maxActive: MAX_ACTIVE,
    acquire,
    update,
    isFinished,
    release,
    dispose,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps game events to flash-panel spawn args.
 *
 *  'death'       → white flash (no token / token === 'white')
 *  'item_charge' → red flash (token === 'red' — orbital cannon charge)
 *  all others    → null
 *
 * Variant token:
 *   The pool reads args.token in acquire() to select the color:
 *     'red'             → COLOR_RED (1, 0.3, 0.3)
 *     'white'/undefined → COLOR_WHITE (1, 1, 1)
 */
export function resolveFlashPanelForEvent(
  event: GameEvent,
): { poolId: 'flash-panel'; args: ComicVFXSpawnArgs } | null {
  switch (event.type) {
    case 'death':
      return {
        poolId: 'flash-panel',
        args: { token: 'white' },
      };

    case 'item_charge':
      // Orbital cannon charge → red flash
      return {
        poolId: 'flash-panel',
        args: { token: 'red' },
      };

    default:
      return null;
  }
}
